import moment from 'moment-timezone';

import type { ScheduledSiteInfo } from '../store/reducers/schedulesites';
import type { SiteScheduleInfo } from './siteSchedule';
import { Site, type SelfieSignature } from './classes';
import type { PopulatedQuestionnaireIndex, ScheduleTypes } from '../typing/types';
import type { ScheduleListItemData, ScheduleItemStatus } from '../typing/scheduleItem';

// ---------------------------------------------------------------------------
// Local types mirroring nimbly-common ActiveScheduleDetails / SiteScheduleDetail
// (audit-web does not import from @nimbly-technologies/nimbly-common)
// ---------------------------------------------------------------------------

export interface ActiveScheduleSiteDetail {
  scheduleID: string;
  questionnaireID: string;
  questionnaireName: string;
  scheduledDate: string;
  totalQuestion: number;
  totalAnswered: number;
  /** 'adhoc' | 'scheduled' */
  scheduleType: string;
  allowAdhoc: boolean;
  hasDraft: boolean;
  firebaseReportID: string;
  isMultiSite: boolean;
  siteID: string;
  siteName: string;
  /** 'not-started' | 'in-progress' | 'completed' | 'overdue' | 'none' */
  status: string;
  startTime: number;
  endTime: number;
  selfieSignatures?: SelfieSignature[];
  signatures?: number;
  emailTargets: string[];
  siteCoordinates?: { latitude: number; longitude: number } | null;
  siteRadius?: string;
  siteTimezone?: string | null;
  isPendingUpload?: boolean;
  schedulePeriod?: string;
}

export interface ActiveScheduleDetailsByDate {
  date: string;
  schedules: {
    allSchedules: ActiveScheduleSiteDetail[];
  };
}

/** Shape returned by GET /v1.0/schedules/lite/v2 → data.data */
export interface ActiveScheduleResponse {
  totalPercentage: number;
  details: ActiveScheduleDetailsByDate[];
}

export function getScheduleListItemIdentity(
  item: Pick<ScheduleListItemData, 'scheduleID' | 'questionnaireID' | 'siteID' | 'scheduleType' | 'scheduledDate'>
): string {
  return [item.scheduleID, item.questionnaireID, item.siteID, item.scheduleType, item.scheduledDate].join('|');
}

// ---------------------------------------------------------------------------
// Internal accumulator used while grouping per-site data across dates
// ---------------------------------------------------------------------------

interface SiteAccumulator {
  siteKey: string;
  siteName: string;
  siteTimezone: string | null;
  siteCoordinates: { latitude: number; longitude: number } | null;
  siteRadius: string | null;
  signatures: number;
  isMultiSite: boolean;
  emailTargets: string[];
  selfieSignatures: SelfieSignature[];
  scheduledCount: number;
  completedCount: number;
  isDraft: boolean;
  isPendingUpload: boolean;
  isCompletedToday: boolean;
  isDueToday: boolean;
}

function buildSiteProfile(acc: SiteAccumulator): Site {
  return new Site(
    acc.siteName,
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    acc.siteCoordinates ?? null,
    -1,
    '',
    '',
    acc.emailTargets,
    '',
    {},
    [],
    [],
    acc.signatures,
    acc.isMultiSite,
    acc.siteTimezone ?? null,
    null,
    acc.siteRadius ?? null
  );
}

// ---------------------------------------------------------------------------
// Mapper 1: ActiveScheduleResponse → ScheduledSiteInfo[]
// Used by getMyScheduledSites thunk (home schedule list)
// ---------------------------------------------------------------------------

/**
 * Maps GET /v1.0/schedules/lite/v2 response to ScheduledSiteInfo[].
 * Groups schedule details by siteID across all dates and aggregates counts.
 *
 * @param activeSchedule - parsed API response body (data.data)
 * @returns ScheduledSiteInfo[] compatible with existing Redux state shape
 */
export function mapActiveScheduleToScheduledSites(activeSchedule: ActiveScheduleResponse): ScheduledSiteInfo[] {
  const today = moment().locale('en').format('YYYY-MM-DD');
  const siteMap = new Map<string, SiteAccumulator>();

  for (const dateEntry of activeSchedule.details ?? []) {
    const isToday = dateEntry.date === today;
    const allSchedules = dateEntry.schedules?.allSchedules ?? [];

    for (const detail of allSchedules) {
      const siteId = detail.siteID;

      if (!siteMap.has(siteId)) {
        siteMap.set(siteId, {
          siteKey: siteId,
          siteName: detail.siteName ?? '',
          siteTimezone: detail.siteTimezone ?? null,
          siteCoordinates: detail.siteCoordinates ?? null,
          siteRadius: detail.siteRadius ?? null,
          signatures: detail.signatures ?? 0,
          isMultiSite: detail.isMultiSite ?? false,
          emailTargets: detail.emailTargets ?? [],
          selfieSignatures: detail.selfieSignatures ?? [],
          scheduledCount: 0,
          completedCount: 0,
          isDraft: false,
          isPendingUpload: false,
          isCompletedToday: false,
          isDueToday: false
        });
      }

      const acc = siteMap.get(siteId)!;
      const isScheduled = detail.scheduleType === 'scheduled';

      if (isScheduled) {
        acc.scheduledCount++;
        if (detail.status === 'completed') {
          acc.completedCount++;
          if (isToday) {
            acc.isCompletedToday = true;
          }
        }
        if (isToday) {
          acc.isDueToday = true;
        }
      }

      if (detail.hasDraft) {
        acc.isDraft = true;
      }
      if (detail.isPendingUpload) {
        acc.isPendingUpload = true;
      }
    }
  }

  const result: ScheduledSiteInfo[] = [];

  for (const acc of siteMap.values()) {
    const siteProfile = buildSiteProfile(acc);
    result.push({
      siteKey: acc.siteKey,
      siteProfile,
      isScheduledThisPeriod: acc.scheduledCount > 0,
      scheduledCount: acc.scheduledCount,
      completedCount: acc.completedCount,
      isDraft: acc.isDraft,
      downloadStatus: 'downloaded',
      isCompletedThisPeriod: acc.scheduledCount > 0 && acc.completedCount >= acc.scheduledCount,
      isCompletedToday: acc.isCompletedToday,
      isMakeUp: false,
      isDueToday: acc.isDueToday,
      isPendingUpload: acc.isPendingUpload,
      skuScheduledCount: 0,
      skuCompletedCount: 0,
      yesterdayActiveScheduleCount: 0,
      yesterdayActiveCompleteCount: 0
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Mapper 2: ActiveScheduleResponse → SiteScheduleInfo[]
// Used by siteSchedule saga (single-site schedule detail)
// ---------------------------------------------------------------------------

/**
 * Maps GET /v1.0/schedules/lite/v2 response (filtered to a single siteKey)
 * to SiteScheduleInfo[] compatible with the siteSchedule Redux slice.
 *
 * Groups SiteScheduleDetail records by scheduleID across all dates.
 */
// Extended shape that satisfies both SiteScheduleInfo and UserSingleSiteScheduleInfo
// (the saga stores this as UserSingleSiteScheduleInfo[] via @ts-ignore)
export interface ExtendedSiteScheduleInfo extends SiteScheduleInfo {
  siteDateTime: string;
  allowAdhoc: boolean;
  hasStrictTime: boolean;
  isAdhocOnly: boolean;
  isAdhocToday: boolean;
  isMultiSite: boolean;
  isYesterdayActiveSchedule: boolean;
  isYesterdayActiveScheduleDone: boolean;
  isYesterdaySchedule: boolean;
  startDate: string;
  endDate: string;
  datesCustomInfo: { customID: string; startDate: Date; endDate: Date }[];
  enforceCheckIn: boolean;
  enforceCheckOut: boolean;
  /** Firebase questionnaire key — used by the saga to enrich questionnaireDetails */
  questionnaireID: string;
}

export function mapActiveScheduleToSiteScheduleInfos(
  activeSchedule: ActiveScheduleResponse,
  siteKey: string
): ExtendedSiteScheduleInfo[] {
  const today = moment().locale('en').format('YYYY-MM-DD');
  const yesterday = moment().subtract(1, 'day').locale('en').format('YYYY-MM-DD');

  // Group per scheduleID
  const scheduleMap = new Map<
    string,
    { detail: ActiveScheduleSiteDetail; dates: { date: string; isComplete: boolean }[] }
  >();

  for (const dateEntry of activeSchedule.details ?? []) {
    const siteSchedules = (dateEntry.schedules?.allSchedules ?? []).filter(d => d.siteID === siteKey);
    for (const detail of siteSchedules) {
      const key = detail.scheduleID;
      if (!scheduleMap.has(key)) {
        scheduleMap.set(key, { detail, dates: [] });
      }
      scheduleMap.get(key)!.dates.push({
        date: dateEntry.date,
        isComplete: detail.status === 'completed'
      });
    }
  }

  const result: ExtendedSiteScheduleInfo[] = [];

  for (const [scheduleID, { detail, dates }] of scheduleMap.entries()) {
    const scheduledDates: { [date: string]: { isComplete: boolean } } = {};
    const unfinishedDates: string[] = [];

    for (const { date, isComplete } of dates) {
      scheduledDates[date] = { isComplete };
      if (!isComplete) {
        unfinishedDates.push(date);
      }
    }

    const todayEntry = dates.find(d => d.date === today);
    const yesterdayEntry = dates.find(d => d.date === yesterday);
    const isAdhocOnly = detail.scheduleType === 'adhoc';
    const totalScheduled = !isAdhocOnly ? dates.length : 0;
    const totalCompleted = dates.filter(d => d.isComplete).length;

    // startTime/endTime are minutes from midnight; hasStrictTime iff the window is non-trivial
    const startTime = detail.startTime ?? 0;
    const endTime = detail.endTime ?? 0;
    const hasStrictTime = startTime > 0 || endTime > 0;

    // siteDateTime: current moment in the site's timezone (used by useScheduleOptions)
    const siteTimezone = detail.siteTimezone ?? null;
    const siteDateTime = siteTimezone ? moment.tz(new Date(), siteTimezone).toISOString() : new Date().toISOString();

    // Yesterday-active: yesterday exists and is not completed
    const isYesterdayActiveSchedule = !!yesterdayEntry && !yesterdayEntry.isComplete;
    const isYesterdayActiveScheduleDone = !!yesterdayEntry && yesterdayEntry.isComplete;

    // Earliest and latest dates in this schedule window
    const sortedDates = [...dates].map(d => d.date).sort();
    const startDate = sortedDates[0] ?? today;
    const endDate = sortedDates[sortedDates.length - 1] ?? today;

    result.push({
      key: scheduleID,
      hasDeadline: hasStrictTime,
      startTime,
      endTime,
      isScheduledToday: !!todayEntry,
      isDoneToday: todayEntry?.isComplete ?? false,
      unfinishedDates,
      isQuestionnaireExists: !!detail.questionnaireName,
      questionnaireTitle: detail.questionnaireName ?? '',
      questionnaireDetails: null as unknown as { key: string; value: PopulatedQuestionnaireIndex },
      scheduledDates,
      totalScheduled,
      totalCompleted,
      type: 'daily' as ScheduleTypes,
      emailTargets: detail.emailTargets ?? [],
      signatures: detail.signatures ?? 0,
      selfieSignatures: detail.selfieSignatures ?? [],
      hasAdhocCustom: false,
      yesterdayActiveScheduleCount: isYesterdayActiveSchedule ? 1 : 0,
      yesterdayActiveCompleteCount: isYesterdayActiveScheduleDone ? 1 : 0,
      // Extended fields required by UserSingleSiteScheduleInfo / useScheduleOptions
      siteDateTime,
      allowAdhoc: detail.allowAdhoc ?? false,
      hasStrictTime,
      isAdhocOnly,
      isAdhocToday: false,
      isMultiSite: detail.isMultiSite ?? false,
      isYesterdayActiveSchedule,
      isYesterdayActiveScheduleDone,
      isYesterdaySchedule: false,
      startDate,
      endDate,
      datesCustomInfo: [],
      enforceCheckIn: false,
      enforceCheckOut: false,
      questionnaireID: detail.questionnaireID ?? ''
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Mapper 3: ActiveScheduleResponse → ScheduleListItemData[]
// Used by new home screen (flat schedule list, no site grouping)
// ---------------------------------------------------------------------------

function mapApiStatus(status: string): ScheduleItemStatus {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'in-progress':
      return 'in-progress';
    case 'overdue':
      return 'overdue';
    default:
      return 'not-started';
  }
}

/**
 * Maps GET /v1.0/schedules/lite/v2 response to a flat array of ScheduleListItemData.
 * Each schedule entry becomes one item — NO site grouping.
 * Sorted by scheduledDate ASC, then startTime ASC.
 */
export function mapActiveScheduleToScheduleItems(activeSchedule: ActiveScheduleResponse): ScheduleListItemData[] {
  const items: ScheduleListItemData[] = [];

  for (const dateEntry of activeSchedule.details ?? []) {
    const allSchedules = dateEntry.schedules?.allSchedules ?? [];

    for (const detail of allSchedules) {
      const startTime = detail.startTime ?? 0;
      const endTime = detail.endTime ?? 0;

      items.push({
        scheduleID: detail.scheduleID,
        questionnaireID: detail.questionnaireID,
        questionnaireName: detail.questionnaireName ?? '',
        siteID: detail.siteID,
        siteName: detail.siteName ?? '',
        isMultiSite: detail.isMultiSite ?? false,
        siteCoordinates: detail.siteCoordinates ?? null,
        scheduledDate: dateEntry.date,
        startTime,
        endTime,
        siteTimezone: detail.siteTimezone ?? null,
        isStartAnytime: startTime === 0 && endTime === 0,
        status: mapApiStatus(detail.status),
        totalQuestion: detail.totalQuestion ?? 0,
        totalAnswered: detail.totalAnswered ?? 0,
        scheduleType: detail.scheduleType === 'adhoc' ? 'adhoc' : 'scheduled',
        allowAdhoc: detail.allowAdhoc ?? false,
        hasDraft: detail.hasDraft ?? false,
        isPendingUpload: detail.isPendingUpload ?? false,
        firebaseReportID: detail.firebaseReportID ?? '',
        emailTargets: detail.emailTargets ?? [],
        schedulePeriod: detail.schedulePeriod,
        siteRadius: detail.siteRadius,
        signatures: detail.signatures ?? 0,
        selfieSignatures: (detail.selfieSignatures as ScheduleListItemData['selfieSignatures']) ?? [],
        hasStrictTime: startTime > 0 || endTime > 0
      });
    }
  }

  // Sort by status priority (in-progress first), then date ASC, then startTime ASC
  const STATUS_PRIORITY: Record<string, number> = {
    'in-progress': 0,
    'not-started': 1,
    overdue: 2,
    completed: 3
  };

  items.sort((a, b) => {
    const statusA = STATUS_PRIORITY[a.status] ?? 9;
    const statusB = STATUS_PRIORITY[b.status] ?? 9;
    if (statusA !== statusB) return statusA - statusB;
    const dateCompare = a.scheduledDate.localeCompare(b.scheduledDate);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime - b.startTime;
  });

  return items;
}
